import React, { useEffect, useMemo, useState } from "react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid2, Grid4, TableChip, BtnRow, BtnPrimary, BtnSecondary,
  BtnLight, InputLabel, FormInput, FormSelect, FormTextarea, HelpText, EmptyState, TableWrap, Th, Td,
} from "../components/ui";

const PRIORITY_OPTIONS = [
  "Pedagogy Improvement","Classroom Management","Assessment Practices","Lesson Planning",
  "Student Engagement","Subject Mastery","Documentation & Reporting","Professional Conduct",
  "Time Management","Technology Integration","Communication with Parents","Custom",
];
const EXECUTION_OPTIONS = [
  "Coaching","Observation-Based Support","Mentoring","Review Meeting","Peer Support","Training Intervention","Monitoring-Based",
];
const FREQUENCY_OPTIONS = ["Daily","Twice a Week","Weekly","Fortnightly","Monthly","Need-Based"];
const STATUS_OPTIONS = ["Not Started","In Progress","On Track","Delayed","Completed"];
const TRAINING_FOR_OPTIONS = ["All Teachers","Subject HODs","New Teachers","Custom (type below)"];
const TRAINING_MODE_OPTIONS = ["Online","Offline","Blended","Internal Workshop","External Program"];
const NOMINATION_OPTIONS = ["Not Nominated","Nominated","Approved"];
const COMPLETION_OPTIONS = ["Not Started","In Progress","Completed"];

export default function Module8TeacherDevelopment() {
  const {
    module8DevPlans,
    setModule8DevPlans,
    module8TrainingPlans,
    setModule8TrainingPlans,
    getAllStaff,
    module8Loading,
    module8LoadError,
    canEditModules1to5,
    loadModule8TeacherDevelopmentTraining,
    saveModule8TeacherDevelopmentTraining,
    resetModule8TeacherDevelopmentTraining,
  } = useACPP();

  const staffList = useMemo(() => getAllStaff(), [getAllStaff]);

  // ── Dev plan form ────────────────────────────────────────────────────────
  const [devForm, setDevForm] = useState({
    priorityArea: "", customPriorityArea: "", executionType: "", responsible: "",
    frequency: "", timeline: "", status: "", needTraining: "", reviewDate: "",
    problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "",
  });
  const setDev = (f) => (e) => setDevForm(prev => ({ ...prev, [f]: e.target.value }));

  // ── Training form ────────────────────────────────────────────────────────
  const [trainForm, setTrainForm] = useState({
    trainingFor: "", trainingForCustom: "", trainingForStaff: "",
    trainingNeedArea: "", trainingTitle: "", trainingProvider: "",
    mode: "", deadline: "", nominationStatus: "", completionStatus: "", followupAction: "",
  });
  const setTrain = (f) => (e) => setTrainForm(prev => ({ ...prev, [f]: e.target.value }));

  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterPriority, setFilterPriority] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [savedSnapshot, setSavedSnapshot] = useState({ devPlans: [], trainingPlans: [] });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");
  const [moduleStatus, setModuleStatus] = useState("Pending");

  const signature = useMemo(
    () => (payload) => JSON.stringify(payload || {}),
    []
  );

  useEffect(() => {
    if (!module8Loading) {
      setSavedSnapshot({
        devPlans: module8DevPlans || [],
        trainingPlans: module8TrainingPlans || [],
      });
    }
  }, [module8Loading, module8DevPlans, module8TrainingPlans]);

  useEffect(() => {
    const currentPayload = { devPlans: module8DevPlans || [], trainingPlans: module8TrainingPlans || [] };
    if (!currentPayload.devPlans.length && !currentPayload.trainingPlans.length && !savedSnapshot.devPlans.length && !savedSnapshot.trainingPlans.length) {
      setModuleStatus("Pending");
      return;
    }
    if (signature(currentPayload) === signature(savedSnapshot)) {
      setModuleStatus("Saved");
      return;
    }
    setModuleStatus("In Progress");
  }, [module8DevPlans, module8TrainingPlans, savedSnapshot, signature]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const getFinalPriority = () => devForm.priorityArea === "Custom" ? devForm.customPriorityArea : (devForm.priorityArea || devForm.customPriorityArea);
  const getFinalTrainingFor = () => {
    if (trainForm.trainingFor.includes("Custom") || !trainForm.trainingFor) return trainForm.trainingForCustom || trainForm.trainingForStaff;
    return trainForm.trainingFor;
  };

  const filterPriorities = [...new Set(module8DevPlans.map(i => i.priorityArea).filter(Boolean))];
  const filterStatuses = [...new Set(module8DevPlans.map(i => i.status).filter(Boolean))];

  const filteredDevPlans = useMemo(() => module8DevPlans.filter(i =>
    (!filterPriority || i.priorityArea === filterPriority) && (!filterStatus || i.status === filterStatus)
  ), [module8DevPlans, filterPriority, filterStatus]);

  // ── Metrics ──────────────────────────────────────────────────────────────
  const priorityCount = [...new Set(module8DevPlans.map(i => i.priorityArea).filter(Boolean))].length;
  const openCount = module8DevPlans.filter(i => i.status !== "Completed").length + module8TrainingPlans.filter(i => i.completionStatus !== "Completed").length;

  // ── Add dev row ──────────────────────────────────────────────────────────
  const addDevRow = () => {
    const priorityArea = getFinalPriority();
    const { problemIdentified, rootCause, actionToBeTaken, executionType, responsible, frequency, timeline, successMetric, status, needTraining, reviewDate } = devForm;
    if (!priorityArea || !problemIdentified || !rootCause || !actionToBeTaken) {
      alert("Please fill: Priority Area, Problem Identified, Root Cause, and Action to be Taken."); return;
    }
    if (!executionType || !responsible || !frequency || !timeline || !successMetric || !status || !needTraining) {
      alert("Please fill: Execution Type, Responsible, Frequency, Timeline, Success Metric, Status, and Training Need."); return;
    }
    setModule8DevPlans(prev => [...prev, { priorityArea, problemIdentified, rootCause, actionToBeTaken, executionType, responsible, frequency, timeline, successMetric, status, needTraining, reviewDate }]);
    setDevForm({ priorityArea: "", customPriorityArea: "", executionType: "", responsible: "", frequency: "", timeline: "", status: "", needTraining: "", reviewDate: "", problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "" });
    setActionError("");
  };

  // ── Add training row ──────────────────────────────────────────────────────
  const addTrainingRow = () => {
    const trainingFor = getFinalTrainingFor();
    const { trainingNeedArea, trainingTitle, trainingProvider, mode, deadline, nominationStatus, completionStatus, followupAction } = trainForm;
    if (!trainingFor || !trainingNeedArea || !trainingTitle || !trainingProvider || !mode || !deadline || !nominationStatus || !completionStatus) {
      alert("Please fill all essential teacher training fields."); return;
    }
    setModule8TrainingPlans(prev => [...prev, { trainingFor, trainingNeedArea, trainingTitle, trainingProvider, mode, deadline, nominationStatus, completionStatus, followupAction }]);
    setTrainForm({ trainingFor: "", trainingForCustom: "", trainingForStaff: "", trainingNeedArea: "", trainingTitle: "", trainingProvider: "", mode: "", deadline: "", nominationStatus: "", completionStatus: "", followupAction: "" });
  };

  const save = async () => {
    if (!module8DevPlans.length && !module8TrainingPlans.length) { alert("Please add at least one teacher development or training row."); return; }
    setSaving(true);
    setActionError("");
    try {
      const saved = await saveModule8TeacherDevelopmentTraining({
        devPlans: module8DevPlans,
        trainingPlans: module8TrainingPlans,
      });
      setSavedSnapshot({
        devPlans: saved?.devPlans || [],
        trainingPlans: saved?.trainingPlans || [],
      });
      alert("Teacher Development & Training Plan saved for current ACPP year.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 8 data.");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setSaving(true);
    setActionError("");
    try {
      await loadModule8TeacherDevelopmentTraining();
    } catch (error) {
      setActionError(error?.message || "Failed to refresh Module 8 data.");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    setSaving(true);
    setActionError("");
    try {
      await resetModule8TeacherDevelopmentTraining();
      setSavedSnapshot({ devPlans: [], trainingPlans: [] });
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 8 data.");
    } finally {
      setSaving(false);
    }
    setDevForm({ priorityArea: "", customPriorityArea: "", executionType: "", responsible: "", frequency: "", timeline: "", status: "", needTraining: "", reviewDate: "", problemIdentified: "", rootCause: "", actionToBeTaken: "", successMetric: "" });
    setTrainForm({ trainingFor: "", trainingForCustom: "", trainingForStaff: "", trainingNeedArea: "", trainingTitle: "", trainingProvider: "", mode: "", deadline: "", nominationStatus: "", completionStatus: "", followupAction: "" });
    setFilterPriority(""); setFilterStatus("");
  };

  return (
    <ModuleCard id="module8">
      <ModuleHeader
        step="Module 8"
        title="Teacher Development & Training Plan"
        description="Create structured teacher development plans and separately track teacher training needs, nominations, completion, and follow-up."
        showStatus={false}
      />

      <InfoBanner>
        Use this module when teaching quality, classroom delivery, or faculty effectiveness needs support. <strong>Responsible persons are pulled live from Module 5</strong> staff mappings. If the issue requires structured capacity building, add a linked training row in the training tracker below.
      </InfoBanner>
      {!!(module8LoadError || actionError) && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">
          {module8LoadError || actionError}
        </div>
      )}

      <SectionDivider />

      <Grid4>
        <MetricCard label="Development Plan Rows" value={module8DevPlans.length} meta="Total teacher-development action rows" />
        <MetricCard label="Priority Areas Used" value={priorityCount} meta="Distinct development priorities" />
        <MetricCard label="Training Rows" value={module8TrainingPlans.length} meta="Training tracker rows created" />
        <MetricCard label="Status" value={moduleStatus} meta={`Open Items: ${openCount}`} />
      </Grid4>

      <SectionDivider />

      {/* Dev Plan Form */}
      {canEditModules1to5 ? (
        <SoftPanel title="Teacher Development Plan Entry Form">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <InputLabel>Priority Area</InputLabel>
            <FormSelect value={devForm.priorityArea} onChange={setDev("priorityArea")}>
              <option value="">Select priority area</option>
              {PRIORITY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Custom Priority Area</InputLabel>
            <FormInput value={devForm.customPriorityArea} onChange={setDev("customPriorityArea")} placeholder="Use only if 'Custom' is selected above" />
          </div>
          <div>
            <InputLabel>Execution Type</InputLabel>
            <FormSelect value={devForm.executionType} onChange={setDev("executionType")}>
              <option value="">Select execution type</option>
              {EXECUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Responsible Person</InputLabel>
            <FormSelect value={devForm.responsible} onChange={setDev("responsible")}>
              <option value="">Select responsible person</option>
              {staffList.map(n => <option key={n} value={n}>{n}</option>)}
              {!staffList.length && <option value="" disabled>— Add staff in Module 5 first —</option>}
            </FormSelect>
            <HelpText>{staffList.length ? `✓ ${staffList.length} staff member(s) loaded from Module 5.` : "ⓘ No staff found — assign teachers/HODs in Module 5 first."}</HelpText>
          </div>
          <div>
            <InputLabel>Frequency</InputLabel>
            <FormSelect value={devForm.frequency} onChange={setDev("frequency")}>
              <option value="">Select frequency</option>
              {FREQUENCY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Timeline</InputLabel>
            <FormInput value={devForm.timeline} onChange={setDev("timeline")} placeholder="Example: 2 weeks / 1 month / Till Term 2" />
          </div>
          <div>
            <InputLabel>Status</InputLabel>
            <FormSelect value={devForm.status} onChange={setDev("status")}>
              <option value="">Select status</option>
              {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Need Training?</InputLabel>
            <FormSelect value={devForm.needTraining} onChange={setDev("needTraining")}>
              <option value="">Select option</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </FormSelect>
          </div>
          <div>
            <InputLabel>Next Review Date</InputLabel>
            <FormInput type="date" value={devForm.reviewDate} onChange={setDev("reviewDate")} />
          </div>
        </div>
        <SectionDivider />
        <Grid2>
          <div>
            <InputLabel>Problem Identified</InputLabel>
            <FormTextarea value={devForm.problemIdentified} onChange={setDev("problemIdentified")} placeholder="What exactly is the issue being observed?" />
          </div>
          <div>
            <InputLabel>Root Cause</InputLabel>
            <FormTextarea value={devForm.rootCause} onChange={setDev("rootCause")} placeholder="Why is this issue happening?" />
          </div>
          <div>
            <InputLabel>Action to be Taken</InputLabel>
            <FormTextarea value={devForm.actionToBeTaken} onChange={setDev("actionToBeTaken")} placeholder="What intervention or action will be taken?" />
          </div>
          <div>
            <InputLabel>Success Metric</InputLabel>
            <FormTextarea value={devForm.successMetric} onChange={setDev("successMetric")} placeholder="How will success be measured?" />
          </div>
        </Grid2>
        <BtnRow>
          <BtnPrimary disabled={!canEditModules1to5 || saving} onClick={addDevRow}>Add Teacher Development Row</BtnPrimary>
          <BtnSecondary disabled={!canEditModules1to5 || saving} onClick={save}>Save Year Data</BtnSecondary>
          <BtnLight disabled={saving} onClick={refresh}>Refresh Year Data</BtnLight>
        </BtnRow>
        </SoftPanel>
      ) : null}

      <SectionDivider />

      {/* Dev Plan Table */}
      <SoftPanel title="Detailed Teacher Development Plan Table">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <InputLabel>Filter by Priority Area</InputLabel>
            <FormSelect value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
              <option value="">All Priority Areas</option>
              {filterPriorities.map(v => <option key={v} value={v}>{v}</option>)}
            </FormSelect>
          </div>
          <div>
            <InputLabel>Filter by Status</InputLabel>
            <FormSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              {filterStatuses.map(v => <option key={v} value={v}>{v}</option>)}
            </FormSelect>
          </div>
        </div>
        <BtnRow>
          <BtnLight onClick={() => { setFilterPriority(""); setFilterStatus(""); }}>Reset Filters</BtnLight>
        </BtnRow>
        <TableWrap>
          <thead>
            <tr>
              <Th>Priority Area</Th><Th>Problem Identified</Th><Th>Root Cause</Th>
              <Th>Action to be Taken</Th><Th>Execution Type</Th><Th>Responsible</Th>
              <Th>Frequency</Th><Th>Timeline</Th><Th>Success Metric</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {filteredDevPlans.length === 0 ? (
              <tr><td colSpan={10}><EmptyState>No teacher development rows match the current filters.</EmptyState></td></tr>
            ) : filteredDevPlans.map((item, i) => (
              <tr key={i}>
                <Td><TableChip>{item.priorityArea}</TableChip></Td>
                <Td>{item.problemIdentified}</Td><Td>{item.rootCause}</Td>
                <Td>{item.actionToBeTaken}</Td><Td>{item.executionType}</Td>
                <Td>{item.responsible}</Td><Td>{item.frequency}</Td>
                <Td>{item.timeline}</Td><Td>{item.successMetric}</Td>
                <Td><TableChip>{item.status}</TableChip></Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      {/* Training Entry Form */}
      {canEditModules1to5 ? (
        <SoftPanel title="Add Training Entry">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <InputLabel>Training For</InputLabel>
              <FormSelect value={trainForm.trainingFor} onChange={setTrain("trainingFor")}>
                <option value="">Select person / group</option>
                {TRAINING_FOR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Custom — Training For</InputLabel>
              <FormInput value={trainForm.trainingForCustom} onChange={setTrain("trainingForCustom")} placeholder="Specific name or group" />
              <HelpText>Or select a specific staff member:</HelpText>
              <FormSelect value={trainForm.trainingForStaff} onChange={setTrain("trainingForStaff")} style={{ marginTop: 6 }}>
                <option value="">Select from Module 5 staff</option>
                {staffList.map(n => <option key={n} value={n}>{n}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Training Need Area</InputLabel>
              <FormInput value={trainForm.trainingNeedArea} onChange={setTrain("trainingNeedArea")} placeholder="What skill or area needs development?" />
            </div>
            <div>
              <InputLabel>Training Title</InputLabel>
              <FormInput value={trainForm.trainingTitle} onChange={setTrain("trainingTitle")} placeholder="Enter training title" />
            </div>
            <div>
              <InputLabel>Training Provider</InputLabel>
              <FormInput value={trainForm.trainingProvider} onChange={setTrain("trainingProvider")} placeholder="Internal / External trainer / Organisation" />
            </div>
            <div>
              <InputLabel>Mode</InputLabel>
              <FormSelect value={trainForm.mode} onChange={setTrain("mode")}>
                <option value="">Select mode</option>
                {TRAINING_MODE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Deadline</InputLabel>
              <FormInput type="date" value={trainForm.deadline} onChange={setTrain("deadline")} />
            </div>
            <div>
              <InputLabel>Nomination Status</InputLabel>
              <FormSelect value={trainForm.nominationStatus} onChange={setTrain("nominationStatus")}>
                <option value="">Select nomination status</option>
                {NOMINATION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </FormSelect>
            </div>
            <div>
              <InputLabel>Completion Status</InputLabel>
              <FormSelect value={trainForm.completionStatus} onChange={setTrain("completionStatus")}>
                <option value="">Select completion status</option>
                {COMPLETION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </FormSelect>
            </div>
          </div>
          <div className="mt-4">
            <InputLabel>Follow-up Action</InputLabel>
            <FormTextarea value={trainForm.followupAction} onChange={setTrain("followupAction")} placeholder="Example: Demo class / Review / Reflection note" />
          </div>
          <BtnRow>
            <BtnPrimary disabled={!canEditModules1to5 || saving} onClick={addTrainingRow}>Add Training Row</BtnPrimary>
          </BtnRow>
        </SoftPanel>
      ) : null}

      {/* Training Records Table */}
      <SoftPanel title="Training Records">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">
          All teacher training entries for the active ACPP year, including nomination and completion status.
        </p>
        <TableWrap>
          <thead>
            <tr>
              <Th>Training For</Th><Th>Training Need Area</Th><Th>Training Title</Th>
              <Th>Provider</Th><Th>Mode</Th><Th>Deadline</Th>
              <Th>Nomination</Th><Th>Completion</Th><Th>Follow-up</Th>
            </tr>
          </thead>
          <tbody>
            {module8TrainingPlans.length === 0 ? (
              <tr><td colSpan={9}><EmptyState>No teacher training rows added yet.</EmptyState></td></tr>
            ) : module8TrainingPlans.map((item, i) => (
              <tr key={i}>
                <Td>{item.trainingFor}</Td><Td>{item.trainingNeedArea}</Td>
                <Td>{item.trainingTitle}</Td><Td>{item.trainingProvider}</Td>
                <Td>{item.mode}</Td><Td>{item.deadline}</Td>
                <Td><TableChip>{item.nominationStatus}</TableChip></Td>
                <Td><TableChip>{item.completionStatus}</TableChip></Td>
                <Td>{item.followupAction || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>
    </ModuleCard>
  );
}
